import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { AddEmailResponse } from "@/Common/Models/Authentication/Login";
import { ISaveEmailOptions } from "@/Common/Models/Employee/ISaveEmailOptions";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { Email } from "../Contacts/Dto/Email";
import { IGetEmployeeEmailResponse } from "../Employee/Dto/IGetEmployeeEmailResponse";
import { SaveEmployeeEmailsResponse } from "../Employee/Dto/SaveEmployeeEmailsResponse";

interface IAddEmailResponse {
    emailAddress: string;
    contactPointId: string;
    isNotifiedByEmail: boolean;
    isOkToContact: boolean;
    isVerified: boolean;
}

class EmployeeAsyncActions {
    public async addEmail(options: ISaveEmailOptions): Promise<ClientResponse<IAddEmailResponse>> {
        const requestData = {
            Address: options.emailAddress,
            IsNotifiedByEmail: options.isNotifiedByEmail,
            IsOkToContact: options.isOkToContact
        };

        const response = await Client.postAsync<AddEmailResponse>(ServerOperations.Server.Myle_User_Email_Insert, requestData);

        if (!response.isSuccess)
            return new ClientResponse(null, this.getTranslatedErrors(response));

        sessionStore.dispatch(
            LocalOperations.Login.AddEmailSuccess,
            {
                emailAddress: response.data.EmailAddress,
                contactPointId: response.data.ContactPointId
            }
        );

        return new ClientResponse<IAddEmailResponse>(
            {
                emailAddress: response.data.EmailAddress,
                contactPointId: response.data.ContactPointId,
                isNotifiedByEmail: response.data.IsNotifiedByEmail,
                isOkToContact: response.data.IsOkToContact,
                isVerified: false
            },
            []
        );
    }

    public async saveEmails(options: ISaveEmailOptions, password?: string): Promise<ClientResponse<IAddEmailResponse>> {
        if (sessionStore.isRecoveryEmailRequired && IsNullOrWhiteSpace(options.emailAddress))
            throw new Error("Email address is required.");

        const requestData = {
            Emails: [],
            IsNotifiedByEmail: options.isNotifiedByEmail,
            UserId: sessionStore.userId,
            Password: password
        };

        if (!IsNullOrWhiteSpace(options.emailAddress)) {
            const newEmail = new Email({
                Address: options.emailAddress,
                IsOkToContact: options.isOkToContact,
                ContactPoint_id: options.contactPointId,
                IsValid: true,
                IsPreferred: true
            });

            requestData.Emails = [newEmail]; // Myle only supports one email for users
        }

        const response = await Client.postAsync<SaveEmployeeEmailsResponse>(ServerOperations.Server.Myle_Employee_Save_Emails, requestData);

        if (!response.isSuccess)
            return new ClientResponse(null, this.getTranslatedErrors(response));

        return new ClientResponse<IAddEmailResponse>(
            {
                emailAddress: response.data.PreferredEmail?.Address,
                contactPointId: response.data.PreferredEmail?.ContactPoint_id,
                isOkToContact: response.data.PreferredEmail?.IsOkToContact,
                isNotifiedByEmail: response.data.IsNotifiedByEmail ?? false,
                isVerified: response.data.PreferredEmail?.IsVerified ?? false
            },
            []
        );
    }

    public async verifyEmail(
        emailAddress: string,
        contactPointId: string,
        verificationCode: string
    ): Promise<ClientResponse> {
        const requestData = {
            Address: emailAddress,
            ContactPointId: contactPointId,
            VerificationCode: verificationCode
        };

        const response = await Client.postAsync(ServerOperations.Server.Myle_Verify_Email, requestData);

        if (!response.isSuccess)
            return new ClientResponse(null, this.getTranslatedErrors(response));

        return response;
    }

    public async getCurrentEmployeeEmails(): Promise<IGetEmployeeEmailResponse> {
        const response = await Client.postAsync<IGetEmployeeEmailResponse>(ServerOperations.Server.Myle_Employee_Get_Emails, {});

        if (!response.isSuccess) {
            return null;
        }

        return response.data;
    }

    private getTranslatedErrors<T>(response: ClientResponse<T>): ErrorMessage[] {
        return response.errors?.filter(p => !IsNullOrWhiteSpace(p.Code)) ?? [];
    }
}

export { EmployeeAsyncActions, IAddEmailResponse };
